import { useEffect, useState } from 'react';

export type ProvinceT = {
  id: number;
  name_th: string;
  name_en: string;
};

export type DistrictT = {
  id: number;
  name_th: string;
  name_en: string;
  province_id: number;
};

export type SubDistrictT = {
  id: number;
  name_th: string;
  name_en: string;
  district_id: number;
  zip_code: number;
};

let cachedProvinces: ProvinceT[] | null = null;
let cachedDistricts: DistrictT[] | null = null;
let cachedSubDistricts: SubDistrictT[] | null = null;

const loadJson = async <T>(url: string): Promise<T[]> => {
  const res = await fetch(url);
  return res.json();
};

const useThaiLocation = () => {
  const [provinces, setProvinces] = useState<ProvinceT[]>([]);
  const [allDistricts, setAllDistricts] = useState<DistrictT[]>([]);
  const [allSubDistricts, setAllSubDistricts] = useState<SubDistrictT[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!cachedProvinces) {
        const [p, d, s] = await Promise.all([
          loadJson<ProvinceT>('/data/provinces.json'),
          loadJson<DistrictT>('/data/districts.json'),
          loadJson<SubDistrictT>('/data/sub_districts.json'),
        ]);
        cachedProvinces = p;
        cachedDistricts = d;
        cachedSubDistricts = s;
      }
      setProvinces(cachedProvinces);
      setAllDistricts(cachedDistricts!);
      setAllSubDistricts(cachedSubDistricts!);
      setLoaded(true);
    };
    load();
  }, []);

  const getDistrictsByProvince = (provinceName: string): DistrictT[] => {
    const province = provinces.find(
      (p) => p.name_th === provinceName || p.name_en === provinceName,
    );
    if (!province) return [];
    return allDistricts.filter((d) => d.province_id === province.id);
  };

  const getSubDistrictsByDistrict = (districtName: string, provinceName: string): SubDistrictT[] => {
    const province = provinces.find(
      (p) => p.name_th === provinceName || p.name_en === provinceName,
    );
    if (!province) return [];
    const district = allDistricts.find(
      (d) => d.province_id === province.id
        && (d.name_th === districtName || d.name_en === districtName),
    );
    if (!district) return [];
    return allSubDistricts.filter((s) => s.district_id === district.id);
  };

  const getZipCode = (subDistrictName: string, districtName: string, provinceName: string): string => {
    const subs = getSubDistrictsByDistrict(districtName, provinceName);
    const sub = subs.find(
      (s) => s.name_th === subDistrictName || s.name_en === subDistrictName,
    );
    return sub ? String(sub.zip_code) : '';
  };

  return {
    loaded,
    provinces,
    getDistrictsByProvince,
    getSubDistrictsByDistrict,
    getZipCode,
  };
};

export default useThaiLocation;
